/**
 * Application shell — DESIGN.md §13.1, §13.6.
 *
 * "Left sidebar: My Tasks, Inbox (unread badge), Search, Views, Reports,
 * projects list, Admin (admins only). Collapsible; becomes a drawer on
 * mobile."
 *
 * This file is now only the *frame*: which of the two navs is on screen, where
 * the skip link goes, and where focus lands after a navigation. The contents
 * live in `components/shell/sidebar-nav.tsx` so the drawer and the pinned rail
 * cannot drift apart.
 *
 * Long-Day Review corrections: the sidebar collapses to a 3.5rem rail and the
 * choice persists (SHELL-1); it is sticky, so it no longer scrolls away on
 * /reports and /admin/lookups (SHELL-2); it becomes a drawer below `lg` rather
 * than `md`, which stops iPad portrait and landscape phones from paying 16rem
 * for it (SHELL-14, MOB-4, MOB-14); and "Skip to content" is the first thing
 * you reach with Tab (SHELL-9).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Menu, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TimerChip } from '@/components/timer-chip';
import { MobileDrawer } from '@/components/shell/mobile-drawer';
import { SidebarNav, MOD_KEY } from '@/components/shell/sidebar-nav';
import { TooltipProvider } from '@/components/shell/tooltip';
import { usePinnedSidebar, useRoomySidebar } from '@/components/shell/use-media-query';
import { PREF_KEYS, parseBoolean, serializeBoolean, usePersistedState } from '@/lib/prefs';
import { newCardTarget, useCurrentProjectKey } from '@/lib/current-project';
import { emitUi } from '@/lib/ui-events';
import { cn } from '@/lib/utils';

/**
 * First visit on a laptop starts collapsed. Below this an expanded sidebar is a
 * quarter of the screen, and the review measured the cost in content width;
 * above it, the labels are worth their space. Read once, at import, so it stays
 * a *default* — resizing never overrides a deliberate choice.
 *
 * 1220 rather than the old 1100 so it agrees with `ROOMY_SIDEBAR_QUERY` (#51):
 * one number for "is there room to push the content across", used both to pick
 * the first-visit default and to decide whether expanding pushes or floats.
 */
const DEFAULT_COLLAPSED = typeof window !== 'undefined' && window.innerWidth < 1220;

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const pinned = usePinnedSidebar();
  const roomy = useRoomySidebar();
  const [collapsed, setCollapsed] = usePersistedState(
    PREF_KEYS.sidebarCollapsed,
    DEFAULT_COLLAPSED,
    parseBoolean,
    serializeBoolean,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  /**
   * #51 — "At 1220px and below, the left hand pane should shrink automatically
   * to icon view. Expanding the left hand pane at this point or below should
   * HOVER the pane above the content."
   *
   * Two states, not one. `collapsed` stays the reader's *preference* and is
   * never written by a resize — the existing rule, and the reason this is a
   * second flag: dragging a window narrow and wide again must give back the
   * sidebar you chose, not the one the window chose for you.
   *
   * `floatOpen` is the transient one. In the 1024–1220 band the rail is always
   * 3.5rem in the flow and the toggle opens the full pane *over* the content,
   * closing on Escape, on a click outside, and on a navigation. It is the
   * drawer's contract with the icons left visible.
   */
  const floating = pinned && !roomy;
  const [floatOpen, setFloatOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const projectKey = useCurrentProjectKey();
  const mainRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  // The *previous* path, not a "have we mounted" flag: React's StrictMode runs
  // mount effects twice in development, and a boolean flag reads the second run
  // as a navigation and steals focus on first paint.
  const lastPath = useRef<string | null>(null);

  /**
   * Navigating closes the drawer; leaving it open over the new screen is the
   * classic mobile-nav bug. Rotating a phone to landscape, or widening past
   * `lg`, also closes it — otherwise the dialog stays trapped over a layout
   * that already has its sidebar.
   */
  useEffect(() => setDrawerOpen(false), [pathname]);
  useEffect(() => {
    if (pinned) setDrawerOpen(false);
  }, [pinned]);

  /**
   * The floating pane closes for the same three reasons the drawer does, plus
   * one of its own: widening past 1220 hands the sidebar back to `collapsed`,
   * and a floating pane left open over a layout that now has room for a real
   * one would be a second sidebar on top of the first.
   */
  useEffect(() => setFloatOpen(false), [pathname]);
  useEffect(() => {
    if (!floating) setFloatOpen(false);
  }, [floating]);
  useEffect(() => {
    if (!floatOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setFloatOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [floatOpen]);

  /**
   * SHELL-10 — after a navigation, focus belongs at the top of the new screen,
   * not on the sidebar link that is now behind you. Two exceptions: never take
   * focus out of something the user is typing into (search-as-you-type
   * navigates on every keystroke), and never fight a Radix dialog that is
   * restoring focus to its own trigger.
   */
  useEffect(() => {
    const previous = lastPath.current;
    lastPath.current = pathname;
    if (previous === null || previous === pathname) return;

    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active.closest('input, textarea, select, [contenteditable="true"]')
    ) {
      return;
    }
    if (document.querySelector('[data-state="open"][role="dialog"]')) return;
    const timer = window.setTimeout(() => mainRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  const focusMain = (): void => mainRef.current?.focus();

  return (
    <TooltipProvider>
      {/*
        #14 — `min-h-dvh` let the shell grow with its content, so the kanban
        board's width escaped to the document and the whole app scrolled
        sideways: the nav rail slid off screen and the window grew its own
        scrollbars. The shell is exactly the viewport now, and `main` below is
        the one thing that scrolls, which is what every page already assumed.
      */}
      <div className="bg-background flex h-dvh overflow-hidden">
        {/* First focusable thing on the page — §13.6, SHELL-9/KEYS-8. */}
        <a
          href="#main"
          onClick={(event) => {
            event.preventDefault();
            focusMain();
          }}
          // Parked just above the viewport rather than `sr-only`: it stays a
          // real, measurable control, and `not-sr-only` would reset the padding
          // that makes it a 44px target when it appears.
          className="bg-primary text-primary-foreground fixed left-3 top-3 z-[60] -translate-y-[200%] rounded-md px-4 py-2.5 text-sm font-medium shadow-lg transition-transform focus:translate-y-0"
        >
          Skip to content
        </a>

        {pinned ? (
          <>
            {/*
              The gutter the layout reserves. In the floating band it is always
              the 3.5rem rail, so the content's width no longer depends on
              whether the sidebar happens to be open — which is the whole point
              of floating rather than pushing.
            */}
            <div
              className={cn(
                'sticky top-0 z-30 h-dvh shrink-0 transition-[width] duration-150',
                floating || collapsed ? 'w-14' : 'w-64',
              )}
            >
              <div
                className={cn(
                  'bg-card flex h-full flex-col border-r transition-[width] duration-150',
                  // Out of the flow only while floating: the expanded pane
                  // overhangs the gutter rather than widening it.
                  floating && 'absolute inset-y-0 left-0',
                  floating && (floatOpen ? 'w-64 shadow-xl' : 'w-14'),
                  !floating && 'w-full',
                )}
              >
                <SidebarNav
                  collapsed={floating ? !floatOpen : collapsed}
                  onToggleCollapsed={
                    floating
                      ? () => setFloatOpen((current) => !current)
                      : () => setCollapsed((current) => !current)
                  }
                  // Floating, a link click should put the pane away — same
                  // reason the drawer does it. Pinned, there is nothing to put
                  // away and the sidebar must stay as the reader left it.
                  {...(floating && floatOpen ? { onNavigate: () => setFloatOpen(false) } : {})}
                  showTimer
                />
              </div>
            </div>

            {/*
              Click-outside, as a layer rather than a document listener: a
              listener has to guess which clicks were "inside", and the pane
              contains two popovers (the user menu, the project list) that
              render into portals outside it.

              Deliberately transparent. A scrim would read as a modal, and this
              is not one — the content behind it is still the thing you are
              working on, and the next click is meant to land on it.
            */}
            {floating && floatOpen ? (
              <button
                type="button"
                tabIndex={-1}
                aria-label="Close navigation"
                className="fixed inset-0 z-20 cursor-default"
                onClick={() => setFloatOpen(false)}
              />
            ) : null}
          </>
        ) : (
          // Only mounted while open: a closed drawer has no tab stops (SHELL-7).
          <MobileDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            returnFocusTo={menuButtonRef}
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {pinned ? null : (
            <header className="bg-background sticky top-0 z-20 flex h-14 shrink-0 items-center gap-1 border-b px-2">
              <Button
                ref={menuButtonRef}
                variant="ghost"
                size="icon"
                aria-label="Open navigation"
                onClick={() => setDrawerOpen(true)}
              >
                <Menu />
              </Button>
              <span className="font-semibold">Slate</span>
              <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-1">
                {/* §6.13 — the timer has to be visible without opening anything. */}
                <TimerChip className="min-w-0 flex-1" />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Search (${MOD_KEY}K)`}
                  onClick={() => emitUi('palette', {})}
                >
                  <Search />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="New card"
                  onClick={() => emitUi('new-card', newCardTarget(projectKey))}
                >
                  <Plus />
                </Button>
              </div>
            </header>
          )}
          <main
            id="main"
            ref={mainRef}
            tabIndex={-1}
            // `min-h-0` so this can be shorter than its content and scroll,
            // rather than stretching the shell it now lives inside (#14).
            className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden outline-none"
          >
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
